<template>
  <Carousel :loading="loading" :data="data" :eu="eu" :colors="colors" />
</template>

<script>
import moment from "moment";
import { conversorFactor } from "@/models/Goals";
import { summationByKey } from "@/models/GoalsArray";
import { summation } from "@/models/HistoricalArray";
import Carousel from "./Carousel.vue";
import { Default } from "@/features/Common/Default.js";
import { parseBRPattern } from "@/models/Utils.js";

export default {
  mixins: [Default],
  components: {
    Carousel,
  },
  props: {
    colors: Array,
    params: Array,
  },
  data() {
    return {
      loading: false,
      data: [],
      eu: "",
      historicalParams: {
        start: moment().format("YYYY-01-01T00:00:00"),
        end: moment().format("YYYY-12-31T23:59:59"),
        interval: "day",
        sum: false,
      },
      timeGoals: {
        startTime: moment().format("YYYY-01-01T00:00:00"),
        endTime: moment().format("YYYY-12-31T23:59:59"),
        interval: "day",
        sum: false,
      },
      goalsParams: {},
      startOfDay: moment().format("YYYY-MM-DDT00:00:00"),
      startOfMonth: moment().startOf("month").format("YYYY-MM-DDT00:00:00"),
    };
  },
  watch: {
    params: {
      immediate: false,
      handler() {
        this.main();
      },
    },
  },
  methods: {
    async main() {
      await this.fetchData();
      let summedGoals = this.sumGoals();
      let summedHistorical = this.sumHistorical();
      let dataHistorical = this.prepareHistorical(summedHistorical);
      let dataGoals = this.prepareGoals(summedGoals);
      let eu = this.getHistoricalEu(true);
      this.eu = eu ? eu : "";
      this.data = this.mountData(dataGoals, dataHistorical);
    },
    setGoalsParams() {
      let historical = [];
      this.component.historicalReferences.forEach((meas) => {
        historical = historical.concat(meas.referenceValue);
      });
      this.goalsParams = {
        plants: this.params,
        historical,
      };
    },
    async fetchData() {
      this.loading = true;
      await this._fetchComponent();
      if (this.component) {
        await this._fetchHistorical();
        await this.component.setHistoricalData(this.historicalParams);
        this.setGoalsParams();
        await this.getGoals(this.goalsParams, this.timeGoals);
      } else {
        console.warn(
          `Component ${this.componentName} from ${this.featureName} is not defined`
        );
      }
      this.loading = false;
    },
    async getGoals(params, time) {
      await this.component.setGoals(params);
      await this.component.setGoalsData(time);
      return this.component.goals;
    },
    sumGoals() {
      if (!this.component) return [];
      return summationByKey(this.component.goals);
    },
    sumHistorical() {
      if (!this.component) return [];
      return summation(this.component.historicalMeasures);
    },
    /*
    This function will sum the values of the periods DTD, MTD, YTD for energy,
    from the beginning of the period to the current day.
    */
    prepareHistorical(summedHistorical) {
      let dataHistorical = { day: 0, month: 0, year: 0 };
      const now = moment().format("YYYY-MM-DDTHH:mm:ss");
      summedHistorical.forEach((data) => {
        if (this.compareDate(data.x, now)) dataHistorical.year += Number(data.y);
        if (this.compareDate(this.startOfMonth, data.x) && this.compareDate(data.x, now))
          dataHistorical.month += Number(data.y);
        if (this.compareDate(this.startOfDay, data.x) && this.compareDate(data.x, now))
          dataHistorical.day += Number(data.y);
      });
      return dataHistorical;
    },
    /*
    This function will sum the values of the periods DTD, MTD, YTD for goals,
    from the beginning of the period to the current day.
    */
    prepareGoals(summedGoals) {
      let dataGoals = [];
      const now = moment().format("YYYY-MM-DDTHH:mm:ss");
      summedGoals.forEach((goal) => {
        let dataGoal = { day: 0, month: 0, year: 0 };
        goal.dataset().forEach((data) => {
          if (this.compareDate(data.x, now)) dataGoal.year += Number(data.y);
          if (
            this.compareDate(this.startOfMonth, data.x) &&
            this.compareDate(data.x, now)
          )
            dataGoal.month += Number(data.y);
          if (this.compareDate(this.startOfDay, data.x) && this.compareDate(data.x, now))
            dataGoal.day += Number(data.y);
        });
        dataGoal = this.calcMinutes(goal, dataGoal);
        dataGoals.push({ id: goal.id, name: goal.name, data: dataGoal });
      });
      return dataGoals;
    },
    getHistoricalEu(notLower) {
      if (!this.component) return;
      let { historicalMeasures } = this.component;
      if (!historicalMeasures || !historicalMeasures.length) return;
      return notLower ? historicalMeasures[0].eu : historicalMeasures[0].eu.toLowerCase();
    },
    getGoalEu() {
      if (!this.component) return;
      let { goals } = this.component;
      if (!goals || !goals.length) return;
      return goals[0].eu.toLowerCase();
    },
    convertEU(value) {
      let energyEu = this.getHistoricalEu();
      let goalEu = this.getGoalEu();
      if (!energyEu || !goalEu) return value;
      return value * conversorFactor[goalEu][energyEu];
    },
    mountData(dataGoals, dataHistorical) {
      if (!dataGoals.length) return this.mountFakeData(dataHistorical);
      dataGoals.forEach((goal) => {
        let data = [];
        let dayGoals = this.convertEU(goal.data.day);
        let monthGoals = this.convertEU(goal.data.month);
        let yearGoals = this.convertEU(goal.data.year);
        data.push(this.mountElement(dataHistorical.day, dayGoals, "DTD"));
        data.push(this.mountElement(dataHistorical.month, monthGoals, "MTD"));
        data.push(this.mountElement(dataHistorical.year, yearGoals, "YTD"));
        goal.data = [...data];
      });
      return dataGoals;
    },
    mountElement(meas, goal, name) {
      let percentage = "-";
      let goalValue = "-";
      let measValue = "-";
      if (goal && meas) percentage = parseBRPattern(((100 * meas) / goal).toFixed(2));
      if (goal) goalValue = parseBRPattern(goal.toFixed(2));
      if (meas) measValue = parseBRPattern(meas.toFixed(2));
      return {
        percentage: `${percentage}`,
        measurement: measValue,
        goal: goalValue,
        name: name,
      };
    },
    calcMinutes(goal, dataGoal) {
      const now = moment().format("YYYY-MM-DDT00:00:00");
      const hourNow = Number(moment().format("HH"));
      const minuteNow = Number(moment().format("mm"));
      const goalNow = goal.values.find((e) => e.timestamp == now);
      const hourValue = goalNow.value / 24;
      const minuteValue = hourValue / 60;
      for (const key in dataGoal) {
        dataGoal[key] += hourValue * hourNow + minuteValue * minuteNow - goalNow.value;
      }
      return dataGoal;
    },
    /*
    This function compare if 'date1' is before 'date2',
    and if the parameters 'equal' be true
    the function return true for 'date1 == date2' too.
    This 'equal' parameter is needed because the difference of data nature energy and goals.
    */
    compareDate(date1, date2) {
      date1 = new Date(date1);
      date2 = new Date(date2);
      if (date1.getTime() <= date2.getTime()) return true;
      return false;
    },
    mountFakeData(dataHistorical) {
      return [
        {
          id: 0,
          name: "Meta",
          data: [
            this.mountElement(dataHistorical.day, 0, "DTD"),
            this.mountElement(dataHistorical.month, 0, "MTD"),
            this.mountElement(dataHistorical.year, 0, "YTD"),
          ],
        },
      ];
    },
  },
};
</script>
